/**
 * wlManual.js — ручной W/L: живой предпросмотр и сброс. [10.19.28]
 *
 * ПОВОД — слова владельца, дословно: «двигаю бегунок окна или уровня и ничего
 * не происходит, пока не нажму кнопку применить… нет визуализации изменений при
 * перемещении бегунка… и нет кнопки сбросить для возврата к исходному значению
 * не только цифр, но и самого изменённого изображения».
 *
 * Так и было: обработчик менял ТОЛЬКО подпись рядом с ползунком, а окно
 * плотности ждало нажатия «Применить». Врач крутит ползунок вслепую, потом
 * жмёт — и видит результат, который уже не с чем сравнить.
 *
 * ЧТО ЗДЕСЬ. Три правила, отделённые от DOM, чтобы их можно было проверить без
 * браузера:
 *
 *   1. КАДРОВОЕ ОГРАНИЧЕНИЕ. Событие input у ползунка приходит на каждое
 *      движение пальца — до сотни раз в секунду. Перерисовывать столько же
 *      нельзя: на телефоне это мгновенно съедает кадр. Правило пропускает
 *      правку не чаще одного раза в кадр (16 мс) и ОБЯЗАТЕЛЬНО отдаёт
 *      последнее значение, даже если оно пришло внутри паузы, — иначе картинка
 *      застынет на предпоследнем положении ползунка, и это выглядит как
 *      залипание.
 *
 *   2. СБРОС — К СОСТОЯНИЮ ДО ПРАВКИ, а не к «заводским 400/40». Разница
 *      принципиальна: врач выбрал пресет «Лёгкие», подвигал ползунок, нажал
 *      сброс — он ждёт возврата К ЛЁГКИМ, а не к абстрактному умолчанию.
 *      Поэтому исходное окно запоминается при ПЕРВОМ касании ползунка (а не
 *      при загрузке страницы) и обновляется каждый раз, когда окно пришло
 *      извне — от пресета, от колеса мыши, от смены серии.
 *
 *   3. ГРАНИЦЫ. Ширина окна ниже единицы бессмысленна (деление на ноль в
 *      формуле показа), поэтому зажимается. Уровень не ограничивается: у КТ он
 *      законно отрицательный, у ПЭТ — тысячи.
 *
 * Куда применять — решено владельцем: ВО ВСЕ СРЕЗЫ, как это делают чипы-пресеты
 * выше по панели. Иначе на одной панели соседние органы управления вели бы себя
 * по-разному: чипы меняли бы всё исследование, а ползунок — одно окно.
 */

/** Наименьшая допустимая ширина окна: при нуле показ вырождается. */
export const МИН_ОКНО = 1;

/** Приводит пару к допустимым значениям. Чистая. */
export function зажать({ ww, wc } = {}) {
  const w = Number(ww), c = Number(wc);
  return {
    ww: Number.isFinite(w) ? Math.max(МИН_ОКНО, Math.round(w)) : NaN,
    wc: Number.isFinite(c) ? Math.round(c) : NaN,
    годно: Number.isFinite(w) && Number.isFinite(c),
  };
}

/**
 * Ограничитель по кадру: пропускает не чаще раза в `шагМс`, но последнее
 * значение доходит всегда.
 *
 * @param {(з:any)=>void} применить
 * @param {{шагМс?: number, часы?: ()=>number, отложить?: (fn:Function, мс:number)=>any}} [нстр]
 * @returns {{подать:(з:any)=>void, ждёт:()=>boolean, сброситьОжидание:()=>void}}
 */
export function поКадрам(применить, нстр = {}) {
  const шаг = Number(нстр.шагМс) > 0 ? Number(нстр.шагМс) : 16;
  const часы = нстр.часы || (() => Date.now());
  const отложить = нстр.отложить || ((fn, мс) => setTimeout(fn, мс));
  let последнееПрименение = -Infinity;
  let хвост = null;      // значение, пришедшее внутри паузы
  let запланировано = false;

  const выпустить = () => {
    запланировано = false;
    if (хвост === null) return;
    const з = хвост;
    хвост = null;
    последнееПрименение = часы();
    применить(з);
  };

  return {
    подать(з) {
      const теперь = часы();
      if (теперь - последнееПрименение >= шаг && !запланировано) {
        последнееПрименение = теперь;
        применить(з);
        return;
      }
      хвост = з;                       // последнее значение не теряется
      if (!запланировано) {
        запланировано = true;
        отложить(выпустить, Math.max(0, шаг - (теперь - последнееПрименение)));
      }
    },
    ждёт: () => хвост !== null,
    сброситьОжидание() { хвост = null; запланировано = false; },
  };
}

/**
 * Память исходного окна для «Сброса».
 *
 * запомнитьИзвне — окно пришло не от ползунка (пресет, колесо, новая серия):
 *   это и есть новая точка возврата.
 * началПравку — первое движение ползунка: если точки возврата ещё нет, ею
 *   становится нынешнее окно.
 */
export function памятьОкна() {
  let исходное = null;
  let правкаИдёт = false;
  return {
    запомнитьИзвне(окно) {
      const з = зажать(окно);
      if (!з.годно) return;
      исходное = { ww: з.ww, wc: з.wc };
      правкаИдёт = false;
    },
    началПравку(нынешнее) {
      if (правкаИдёт) return;
      const з = зажать(нынешнее);
      if (з.годно && !исходное) исходное = { ww: з.ww, wc: з.wc };
      правкаИдёт = true;
    },
    /** @returns {{есть:boolean, окно?:{ww:number,wc:number}, причина?:string}} */
    дляСброса() {
      if (!исходное) return { есть: false, причина: 'возвращать не к чему: окно ещё не менялось' };
      return { есть: true, окно: { ...исходное } };
    },
    сброшено() { правкаИдёт = false; },
    забыть() { исходное = null; правкаИдёт = false; },
  };
}

export default { МИН_ОКНО, зажать, поКадрам, памятьОкна };

/**
 * [11.1.17] Подстроить шкалу ползунка под значение, пришедшее извне
 * (чип PET, авто-окно XA): браузер молча зажимает input.value к max/min, и
 * подпись расходилась с бегунком, а «Применить» обрушивал окно (аудит §2.1).
 * Шкала расширяется, значение не режется. Чистая функция — тестируется
 * заглушкой {min,max} без DOM.
 * @param {{min:any,max:any}} эл
 * @param {number|string} значение
 */
export function подстроитьШкалу(эл, значение) {
  if (!эл) return;
  const v = Number(значение);
  if (!Number.isFinite(v)) return;
  if (эл.max !== '' && эл.max != null && v > Number(эл.max)) эл.max = v;
  if (эл.min !== '' && эл.min != null && v < Number(эл.min)) эл.min = v;
}
